const IMAGE_FIELD_PRIORITY = [
  "main_super_picture_hd",
  "main_picture_hd",
  "main_web_picture",
  "main_hd_web_picture",
  "main_a6_picture",
  "detail_1_url",
  "detail_2_url",
  "frame_picture",
  "perspective_url",
  "signatures_pictures",
  "back_pictures",
  "other_url",
  "NewMainLR",
  "NewMainHR",
] as const;

const WEB_IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif"];

export type ArtworkImageField =
  | (typeof IMAGE_FIELD_PRIORITY)[number]
  | "NewMainLR"
  | "NewMainHR";

export const normalizeArtworkImageValue = (value: unknown): string => {
  if (!value || value === false || value === "false") return "";
  return typeof value === "string" ? value.trim() : "";
};

export const splitArtworkImageUrls = (value: unknown): string[] => {
  const normalized = normalizeArtworkImageValue(value);
  if (!normalized) return [];

  return normalized
    .split(/[\r\n,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
};

export const isTiffImageUrl = (url: string): boolean => {
  const clean = url.trim().toLowerCase().split("?")[0].split("#")[0];
  return clean.endsWith(".tif") || clean.endsWith(".tiff");
};

export const isRenderableArtworkImageUrl = (value: unknown): boolean => {
  const url = normalizeArtworkImageValue(value);
  if (!url || isTiffImageUrl(url)) return false;

  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:image/") ||
    url.startsWith("/") ||
    url.startsWith("./")
  ) {
    return true;
  }

  const lower = url.toLowerCase();
  return WEB_IMAGE_EXTENSIONS.some((ext) => lower.includes(ext));
};

export const getRenderableArtworkImageUrls = (value: unknown): string[] => {
  return splitArtworkImageUrls(value).filter(isRenderableArtworkImageUrl);
};

export const getArtworkImageCandidates = (
  artwork: Partial<Record<ArtworkImageField, unknown>> & {
    image_1920?: unknown;
  },
): string[] => {
  const seen = new Set<string>();
  const candidates: string[] = [];

  for (const field of ["NewMainLR", "NewMainHR"] as const) {
    for (const url of getRenderableArtworkImageUrls(artwork[field])) {
      if (seen.has(url)) continue;
      seen.add(url);
      candidates.push(url);
      return candidates;
    }
  }

  for (const field of IMAGE_FIELD_PRIORITY) {
    if (field === "NewMainLR" || field === "NewMainHR")
      continue;
    for (const url of getRenderableArtworkImageUrls(artwork[field])) {
      if (seen.has(url)) continue;
      seen.add(url);
      candidates.push(url);
    }
  }

  const image1920 = normalizeArtworkImageValue(artwork.image_1920);
  if (image1920) {
    const dataUrl = image1920.startsWith("data:image/")
      ? image1920
      : `data:image/png;base64,${image1920}`;
    if (!seen.has(dataUrl)) {
      candidates.push(dataUrl);
    }
  }

  return candidates;
};

export const getPrimaryArtworkImageUrl = (
  artwork: Partial<Record<ArtworkImageField, unknown>> & {
    image_1920?: unknown;
  },
): string => {
  return getArtworkImageCandidates(artwork)[0] || "";
};
